import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta, eur } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, Diamond, CreditCard, Check, Plus, Calendar, User, Boxes, Users } from "lucide-react";

export default function CollabPackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const t = useT();

  const [doc, setDoc] = useState(undefined);
  const [mode, setMode] = useState("credits");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try { const { data } = await api.get(`/public/collab-offerings/${id}`); setDoc(data); }
    catch { setDoc(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const totalCents = Number(doc?.total_price_cents || 0);
  const creditPrice = useMemo(() => (totalCents > 0 ? Math.ceil(totalCents / 10) : 0), [totalCents]);
  const userCredits = Number(user?.credits || 0);
  const insufficient = mode === "credits" && userCredits < creditPrice;

  useEffect(() => { if (doc) setMode(userCredits >= creditPrice ? "credits" : "stripe"); }, [doc?.id, creditPrice, userCredits]); // eslint-disable-line react-hooks/exhaustive-deps

  const book = async () => {
    if (busy || !doc) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/collab-offerings/${id}/book`, { payment_mode: mode, one_on_one_selections: [], origin_url: window.location.origin });
      if (data?.checkout_url) { window.location.assign(data.checkout_url); return; }
      await refreshUser();
      const hasOneOnOne = (doc.blocks || []).some((b) => b.kind === "one_on_one");
      if (data?.id && hasOneOnOne) { navigate(`/app/collab-package-slots/${data.id}`); return; }
      if (data?.id) { navigate(`/app/collab-package-booked/${data.id}`); return; }
      toast.success(t("cov.booked_title"));
      navigate("/app/bookings");
    } catch (e) { toast.error(apiError(e, t("cov.book_failed"))); }
    finally { setBusy(false); }
  };

  if (doc === undefined) return <Spinner label={t("common.loading")} />;
  if (doc === null) {
    return (
      <div className="space-y-4">
        <Back navigate={navigate} t={t} />
        <p className="text-sm text-muted-foreground">{t("cov.not_found")}</p>
      </div>
    );
  }

  const KIND_ICON = { training: Calendar, one_on_one: User, product: Boxes };

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="collab-package-detail">
      <Back navigate={navigate} t={t} />
      <div>
        <h1 className="font-display text-3xl text-foreground">{doc.title}</h1>
        {doc.description ? <p className="mt-1 text-sm text-muted-foreground">{doc.description}</p> : null}
      </div>

      <div>
        <p className="mb-2 text-sm font-medium text-foreground">{t("cov.coaches")}</p>
        <div className="flex flex-wrap gap-2">
          {(doc.coaches || []).map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground" data-testid={`cov-coach-${c.id}`}>
              <Users className="h-3.5 w-3.5 text-sage-light" /> {c.name}
            </span>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2 text-sm font-medium text-foreground">{t("cov.included")}</p>
        <div className="space-y-2">
          {(doc.blocks || []).map((b) => {
            const Icon = KIND_ICON[b.kind] || Boxes;
            return (
              <div key={b.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`cov-block-${b.id}`}>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-primary" />
                  <p className="flex-1 font-medium text-foreground">{b.title || t(`cov.kind_${b.kind}`)}</p>
                  <p className="font-display text-sm font-semibold text-primary">{eur(b.price_cents || 0)}</p>
                </div>
                {b.description ? <p className="mt-1 text-xs text-muted-foreground">{b.description}</p> : null}
                {b.kind === "one_on_one" && b.duration_min ? <p className="mt-1 text-xs text-muted-foreground">{b.duration_min} {t("cov.min")}</p> : null}
                {b.kind === "training" && b.max_participants > 0 ? (
                  <p className="mt-1 text-xs text-muted-foreground">{t("cov.capacity", { n: Math.max(0, b.max_participants - (b.bookings_count || 0)), total: b.max_participants })}</p>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between rounded-2xl bg-primary p-4 text-primary-foreground">
        <span className="text-sm">{t("cov.total")}</span>
        <span className="font-display text-2xl font-semibold">{eur(totalCents)}</span>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">{t("cov.payment")}</p>
        <div className="flex gap-2">
          <button data-testid="cov-pay-credits" onClick={() => setMode("credits")}
            className={`relative flex-1 rounded-xl border p-4 text-left transition-colors ${mode === "credits" ? "border-primary bg-accent/40" : "border-border bg-card hover:bg-accent/30"}`}>
            <span className="flex items-center gap-2 text-sm font-medium text-foreground"><Diamond className="h-4 w-4" /> {creditPrice}</span>
            <span className={`mt-1 block text-xs ${insufficient ? "text-rose-600" : "text-muted-foreground"}`}>
              {insufficient ? t("cov.insufficient", { need: Math.max(0, creditPrice - userCredits), have: userCredits }) : t("cov.balance", { balance: userCredits })}
            </span>
            {mode === "credits" && !insufficient ? <Check className="absolute right-3 top-3 h-4 w-4 text-primary" /> : null}
          </button>
          <button data-testid="cov-pay-stripe" onClick={() => setMode("stripe")}
            className={`relative flex-1 rounded-xl border p-4 text-left transition-colors ${mode === "stripe" ? "border-primary bg-accent/40" : "border-border bg-card hover:bg-accent/30"}`}>
            <span className="flex items-center gap-2 text-sm font-medium text-foreground"><CreditCard className="h-4 w-4" /> {eur(totalCents)}</span>
            {mode === "stripe" ? <Check className="absolute right-3 top-3 h-4 w-4 text-primary" /> : null}
          </button>
        </div>
        {insufficient ? (
          <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => navigate("/app/credits")} data-testid="cov-add-credits">
            <Plus className="h-4 w-4" /> {t("cov.add_credits")}
          </Button>
        ) : null}
      </div>

      <Button className="w-full rounded-full" size="lg" disabled={busy || doc.fully_booked || insufficient} onClick={book} data-testid="cov-book-package">
        {busy ? t("common.saving") : doc.fully_booked ? t("cov.full") : mode === "credits" ? t("cov.book_credits", { credits: creditPrice }) : t("cov.book_card", { amount: eur(totalCents) })}
      </Button>
    </div>
  );
}

function Back({ navigate, t }) {
  return (
    <button onClick={() => navigate("/app/collab-packages")} data-testid="cov-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ChevronLeft className="h-4 w-4" /> {t("cov.packages_title")}
    </button>
  );
}
